// This is boilerplate code - a wrapper around react-query that makes it easier to use
// for a given api service.
// Previously used chatgpt to get to this point: https://chat.openai.com/share/b31f3c35-7118-42bf-80ff-27fbeaa41c65

import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  QueryKey,
} from '@tanstack/react-query';
import config from 'src/config';
import ApiResponse from './ApiResponse';
import ApiError from './ApiError';

interface UseGetApiParams<TRawResponse, TParsedResponse = TRawResponse> {
  endpoint: string;
  // Optional custom cache key. If omitted, defaults to [endpoint, queryParameters]
  queryKey?: QueryKey;
  queryParameters?:
    | {
        [key: string]: string | boolean | undefined | number | unknown;
      }
    | unknown;
  options?: Omit<
    UseQueryOptions<TParsedResponse | undefined, ApiError>,
    'queryKey' | 'queryFn'
  >;
  transformResponse?: (data: TRawResponse) => TParsedResponse;
  errorHandler?: (e: ApiError) => Error;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toQueryString(params: { [key: string]: any }) {
  const result: string[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function convertToQueryString(obj: any, prefix = '') {
    if (obj === null || obj === undefined) {
      return;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          convertToQueryString(item, `${prefix}[${index}]`);
        } else {
          result.push(`${prefix}[${index}]=${encodeURIComponent(item)}`);
        }
      });
      return;
    }

    // Handle objects
    if (typeof obj === 'object') {
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        const newPrefix = prefix ? `${prefix}[${key}]` : key;

        if (value === null || value === undefined) {
          return;
        } else if (typeof value === 'object') {
          convertToQueryString(value, newPrefix);
        } else {
          result.push(`${newPrefix}=${encodeURIComponent(value)}`);
        }
      });
      return;
    }

    // Handle primitives (should not reach here with our recursion approach)
    result.push(`${prefix}=${encodeURIComponent(obj)}`);
  }

  Object.keys(params).forEach((key) => {
    convertToQueryString(params[key], key);
  });

  return result.join('&');
}

const useGetApi = <TRawResponse = unknown, TParsedResponse = TRawResponse>({
  endpoint,
  queryKey,
  queryParameters,
  options,
  transformResponse,
  errorHandler,
}: UseGetApiParams<TRawResponse, TParsedResponse>): UseQueryResult<
  TParsedResponse | undefined,
  ApiError
> => {
  const finalKey: QueryKey = queryKey ?? [endpoint, queryParameters];

  return useQuery<TParsedResponse | undefined, ApiError>({
    queryKey: finalKey,
    queryFn: async () => {
      const url =
        `${config.apiBaseUrl}/${endpoint}` +
        (queryParameters && Object.keys(queryParameters).length
          ? `?${toQueryString(queryParameters)}`
          : '');

      const response = await fetch(url, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        if (errorHandler) throw errorHandler(errorResponse);
        throw new Error(errorResponse.message);
      }
      const data: ApiResponse<TRawResponse> = await response.json();

      if (transformResponse) {
        return transformResponse(data.data);
      }

      return data.data as unknown as TParsedResponse | undefined;
    },
    ...options,
  });
};

export default useGetApi;
